import React, { useEffect, useRef, useState } from "react";

export const Modal = ({ children, title, isOpen, onClose, onPrev, onNext }) => {
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const bodyRef = useRef(null);

  // Lock body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Escape key: close lightbox first, then modal
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") {
        if (lightboxSrc) setLightboxSrc(null);
        else onClose();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, lightboxSrc, onClose]);

  // Window-level capture listener: fires before Bootstrap/jQuery can intercept.
  // Handles both <img> tags and divs using backgroundImage (e.g. 4point4).
  useEffect(() => {
    if (!isOpen) return;
    const handleCapture = (e) => {
      if (!bodyRef.current) return;

      // Walk up from the click target to the modal body
      let el = e.target;
      while (el && el !== bodyRef.current) {
        // Real <img> element
        if (el.tagName === "IMG") {
          setLightboxSrc(el.src);
          return;
        }
        // Div/element with inline backgroundImage
        if (el.style && el.style.backgroundImage) {
          const match = el.style.backgroundImage.match(/url\(['"]?([^'"()]+)['"]?\)/);
          if (match) {
            setLightboxSrc(match[1]);
            return;
          }
        }
        el = el.parentElement;
      }
    };
    window.addEventListener("click", handleCapture, true);
    return () => window.removeEventListener("click", handleCapture, true);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          zIndex: 1040,
        }}
      />

      {/* Dialog */}
      <div
        style={{
          position: "fixed",
          top: "4%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "92%",
          maxWidth: "860px",
          maxHeight: "90vh",
          backgroundColor: "#fff",
          borderRadius: "4px",
          boxShadow: "0 5px 15px rgba(0,0,0,0.4)",
          zIndex: 1050,
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: "12px 18px",
          borderBottom: "1px solid #e5e5e5",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}>
          <h4 style={{ margin: 0, fontSize: "17px", fontWeight: "normal", letterSpacing: "0.02em" }}>
            {title}
          </h4>
          <button
            onClick={onClose}
            style={{
              background: "none", border: "none", fontSize: "22px",
              cursor: "pointer", lineHeight: 1, padding: "0 4px",
              color: "#888", transition: "color 0.2s",
            }}
            onMouseOver={(e) => e.currentTarget.style.color = "#333"}
            onMouseOut={(e) => e.currentTarget.style.color = "#888"}
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div
          ref={bodyRef}
          className="bbf-person-modal"
          style={{ overflowY: "auto", flex: 1 }}
        >
          {children}
        </div>

        {/* Footer — prev / close / next always visible */}
        <div style={{
          padding: "10px 18px",
          borderTop: "1px solid #e5e5e5",
          flexShrink: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
          {onPrev
            ? <button className="bbf-modal-nav" onClick={onPrev}>&#8249;</button>
            : <span />}
          <button
            onClick={onClose}
            style={{
              padding: "5px 14px", fontSize: "13px", cursor: "pointer",
              border: "1px solid #ccc", borderRadius: "3px", background: "#fff",
              color: "#555",
            }}
          >
            Close
          </button>
          {onNext
            ? <button className="bbf-modal-nav" onClick={onNext}>&#8250;</button>
            : <span />}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxSrc && (
        <div
          onClick={() => setLightboxSrc(null)}
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.92)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
          }}
        >
          <img
            src={lightboxSrc}
            alt=""
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              objectFit: "contain",
              boxShadow: "0 0 60px rgba(0,0,0,0.6)",
              cursor: "default",
            }}
          />
          <button
            onClick={() => setLightboxSrc(null)}
            style={{
              position: "fixed",
              top: "20px", right: "24px",
              background: "none", border: "none",
              color: "rgba(255,255,255,0.7)",
              fontSize: "36px", cursor: "pointer",
              lineHeight: 1, padding: 0,
            }}
            onMouseOver={(e) => e.currentTarget.style.color = "#fff"}
            onMouseOut={(e) => e.currentTarget.style.color = "rgba(255,255,255,0.7)"}
          >
            &times;
          </button>
        </div>
      )}
    </>
  );
};
